import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';

const ACCENT = '#C87533';

/* SVG icons — consistent size, same stroke style */
const ShieldIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const SpeechIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const StarIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const PILLARS_SL = [
  {
    key: 'integrity',
    Icon: ShieldIcon,
    title: 'INTEGRITETA',
    desc: 'Brezkompromisno spoštovanje RSP standardov in 100 % poštenost pri poročanju podatkov s terena.',
  },
  {
    key: 'honesty',
    Icon: SpeechIcon,
    title: 'ISKRENOST',
    desc: 'Transparentna komunikacija z naročnikom po sistemu "brez presenečenj" — realni uvidi, ne glede na rezultat.',
  },
  {
    key: 'excellence',
    Icon: StarIcon,
    title: 'ODLIČNOST',
    desc: 'Vsaka interakcija mora biti premium. Ne dosegamo le kvot, temveč postavljamo najvišje standarde izvedbe.',
  },
];

const PILLARS_EN = [
  {
    key: 'integrity',
    Icon: ShieldIcon,
    title: 'INTEGRITY',
    desc: 'Uncompromising respect for RSP standards and 100% honesty in reporting field data.',
  },
  {
    key: 'honesty',
    Icon: SpeechIcon,
    title: 'TRANSPARENCY',
    desc: 'Open communication with the client on a "no surprises" basis — real insights, regardless of the result.',
  },
  {
    key: 'excellence',
    Icon: StarIcon,
    title: 'EXCELLENCE',
    desc: 'Every interaction must be premium. We don\'t just hit quotas — we set the highest standards of execution.',
  },
];

export const ValuesSection = () => {
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const PILLARS = lang === 'sl' ? PILLARS_SL : PILLARS_EN;

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const st = { trigger: sectionRef.current, start: 'top 68%', once: true };

      // Header
      gsap.fromTo('.val-header',
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', scrollTrigger: st }
      );

      // Cards — each slides up from different depths
      gsap.fromTo('.val-card',
        { opacity: 0, y: 50 },
        {
          opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.14,
          scrollTrigger: { ...st, start: 'top 72%' },
        }
      );

      // Icons — scale pop after card
      gsap.fromTo('.val-icon',
        { scale: 0.5, opacity: 0 },
        {
          scale: 1, opacity: 1, duration: 0.45, ease: 'back.out(2)', stagger: 0.14,
          scrollTrigger: { ...st, start: 'top 70%' },
          delay: 0.3,
        }
      );

      // Tagline
      gsap.fromTo('.val-tagline',
        { opacity: 0, y: 18 },
        {
          opacity: 1, y: 0, duration: 0.6, ease: 'power3.out',
          scrollTrigger: { ...st, start: 'top 60%' },
          delay: 0.6,
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="values"
      className="scroll-section min-h-screen flex items-center px-4 py-20"
    >
      <div className="section-content max-w-[1100px] mx-auto w-full">

        {/* Header */}
        <div className="val-header text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/8">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C87533]" />
            <span className="font-mono text-xs text-[#C87533] uppercase tracking-widest">
              {lang === 'sl' ? 'Naša kultura' : 'Our culture'}
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            {lang === 'sl'
              ? <>Naše <span style={{ color: ACCENT }}>vrednote</span></>
              : <>Our <span style={{ color: ACCENT }}>values</span></>
            }
          </h2>
          <p className="text-white/40 max-w-xl mx-auto text-base leading-relaxed">
            {lang === 'sl'
              ? 'Načela, ki usmerjajo vsako odločitev — na terenu in za mizo.'
              : 'Principles that guide every decision — in the field and at the table.'}
          </p>
        </div>

        {/* 3-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {PILLARS.map((pillar) => (
            <div
              key={pillar.key}
              className="val-card group relative rounded-2xl border border-white/8 bg-white/3 backdrop-blur-sm p-6 flex flex-col gap-5 hover:border-white/15 hover:bg-white/5 transition-all duration-300"
            >
              {/* Top accent line */}
              <div
                className="absolute top-0 left-6 right-6 h-px rounded-full"
                style={{ background: `linear-gradient(90deg, ${ACCENT}70, transparent)` }}
              />
              {/* Hover glow */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: `radial-gradient(ellipse 80% 50% at 50% 0%, ${ACCENT}10 0%, transparent 70%)` }}
              />

              {/* Icon + title row */}
              <div className="relative flex items-center gap-3">
                <div
                  className="val-icon w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${ACCENT}15`,
                    border: `1px solid ${ACCENT}35`,
                    color: ACCENT,
                  }}
                >
                  <pillar.Icon />
                </div>
                <h3
                  className="font-mono text-sm font-bold tracking-widest uppercase"
                  style={{ color: ACCENT }}
                >
                  {pillar.title}
                </h3>
              </div>

              {/* Description */}
              <p className="relative text-white/55 text-sm leading-relaxed">
                {pillar.desc}
              </p>

              {/* Bottom hover line */}
              <div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 h-px w-0 group-hover:w-2/3 transition-all duration-500 rounded-full"
                style={{ background: ACCENT }}
              />
            </div>
          ))}
        </div>

        {/* Tagline */}
        <div
          className="val-tagline relative rounded-2xl border border-[#C87533]/25 bg-[#C87533]/5 backdrop-blur-sm px-8 py-5 text-center overflow-hidden"
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 60% 80% at 50% 50%, rgba(200,117,51,0.07) 0%, transparent 70%)' }}
          />
          <p className="relative font-display text-base sm:text-lg font-semibold text-white/80 leading-relaxed">
            <span className="text-[#C87533] font-bold">Wibe Solutions: </span>
            {lang === 'sl'
              ? 'Zaupanje temelji na dejanjih, ne le na številkah.'
              : 'Trust is built on actions, not just numbers.'}
          </p>
        </div>

      </div>
    </section>
  );
};
